import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowLeft, MessageSquare, Save, RefreshCw, AlertTriangle } from "lucide-react";

const KPI = ({ label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-emerald-100 bg-white p-5">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="mt-1 font-display font-bold text-2xl text-emerald-950 tabular-nums">{value}</div>
  </div>
);

export default function SmsAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";
  const [dash, setDash] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [notes, setNotes] = useState([]);
  const [drafts, setDrafts] = useState({});

  const load = () => {
    api.get("/admin/sms/dashboard").then((r) => setDash(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/sms/templates").then((r) => setTemplates(r.data.templates)).catch(() => {});
    api.get("/admin/sms/notifications").then((r) => setNotes(r.data.notifications)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveTpl = async (t) => {
    const d = drafts[t.event_type] || {};
    try {
      await api.patch(`/admin/sms/templates/${t.event_type}`, {
        body: d.body ?? t.body, active: d.active ?? t.active, sender_id: d.sender_id ?? t.sender_id,
      });
      toast.success(`${t.label} template saved`);
      setDrafts((p) => ({ ...p, [t.event_type]: undefined }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setD = (et, k, v) => setDrafts((p) => ({ ...p, [et]: { ...p[et], [k]: v } }));

  const retry = async (notification_id) => {
    try {
      const r = await api.post("/admin/sms/retry", notification_id ? { notification_id } : {});
      toast.success(notification_id ? "Retry attempted" : `Retried ${r.data.retried} failed message(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusClass = (s) => s === "delivered" ? "bg-emerald-100 text-emerald-800"
    : s === "sent" ? "bg-teal-100 text-teal-800"
    : s === "failed" ? "bg-amber-100 text-amber-800"
    : s === "failed_permanent" ? "bg-red-100 text-red-700"
    : s === "queued" ? "bg-blue-100 text-blue-700"
    : "bg-gray-200 text-gray-600";

  return (
    <div>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <button onClick={() => navigate("/admin")} className="text-emerald-700 flex items-center gap-2 text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Back to Command Centre</button>
        <div className="flex items-center gap-3">
          <MessageSquare className="w-6 h-6 text-emerald-600" />
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Notification Engine</span>
            <h1 className="font-display font-bold text-3xl text-emerald-950">SMS Management</h1>
          </div>
          {dash && <Badge data-testid="sms-mode-badge" className={dash.live ? "bg-emerald-100 text-emerald-800 ml-2" : "bg-amber-100 text-amber-800 ml-2"}>{dash.live ? `LIVE · ${dash.provider}` : "SIMULATED (add gateway key to go live)"}</Badge>}
        </div>

        {!dash ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div> : (
          <>
            {dash.balance != null && (
              <div data-testid="sms-balance-banner" className={`mt-6 flex items-center justify-between flex-wrap gap-3 rounded-2xl border px-5 py-4 ${dash.low_balance ? "border-red-200 bg-red-50" : "border-emerald-100 bg-emerald-50"}`}>
                <div>
                  <span className="text-xs text-gray-500">Sleeng SMS Balance</span>
                  <div data-testid="sms-balance-value" className={`font-display font-bold text-2xl ${dash.low_balance ? "text-red-700" : "text-emerald-800"}`}>₦{Number(dash.balance).toLocaleString()}</div>
                </div>
                {dash.low_balance && (
                  <div className="text-sm text-red-700 font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Low balance — top up your Sleeng account to keep SMS delivering</div>
                )}
              </div>
            )}
            <div className="mt-8 grid gap-4 grid-cols-2 lg:grid-cols-4">
              <KPI testid="sms-kpi-total" label="Total SMS" value={dash.total.toLocaleString()} />
              <KPI testid="sms-kpi-sent" label="Sent (awaiting DLR)" value={dash.sent.toLocaleString()} />
              <KPI testid="sms-kpi-delivered" label="Delivered" value={dash.delivered.toLocaleString()} />
              <KPI testid="sms-kpi-failed" label="Failed" value={dash.failed.toLocaleString()} />
              <KPI testid="sms-kpi-rate" label="Delivery Rate" value={`${dash.delivery_rate}%`} />
              <KPI testid="sms-kpi-queued" label="Queued" value={dash.queued.toLocaleString()} />
              <KPI testid="sms-kpi-suppressed" label="Suppressed" value={dash.suppressed.toLocaleString()} />
              <KPI testid="sms-kpi-simulated" label="Simulated" value={dash.simulated.toLocaleString()} />
              <KPI testid="sms-kpi-cost" label={`Est. Cost (₦${dash.unit_cost}/sms)`} value={`₦${dash.cost.toLocaleString()}`} />
            </div>

            {dash.by_event.length > 0 && (
              <div className="mt-6 rounded-2xl border border-emerald-100 bg-white p-5">
                <h3 className="font-display font-semibold text-emerald-950 mb-3">SMS by Event</h3>
                <div className="flex flex-wrap gap-2">
                  {dash.by_event.map((e) => <Badge key={e.key} className="bg-emerald-50 text-emerald-700 border border-emerald-100">{e.event}: {e.count}</Badge>)}
                </div>
              </div>
            )}

            <h2 className="mt-10 font-display font-bold text-xl text-emerald-950">Message Templates</h2>
            <p className="text-sm text-gray-500 mb-4">{isSuper ? "Edit approved wording. Changes are audit-logged." : "Only the Super Administrator can edit templates."}</p>
            <div className="space-y-4">
              {templates.map((t) => {
                const d = drafts[t.event_type] || {};
                return (
                  <div key={t.event_type} data-testid={`sms-template-${t.event_type}`} className="rounded-2xl border border-emerald-100 bg-white p-5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-emerald-950">{t.label}</span>
                        {t.critical && <Badge className="bg-red-100 text-red-700">Critical</Badge>}
                        <Badge className={(d.active ?? t.active) ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-600"}>{(d.active ?? t.active) ? "Active" : "Inactive"}</Badge>
                      </div>
                      {isSuper && (
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 text-xs text-gray-500">Active <Switch data-testid={`sms-active-${t.event_type}`} checked={d.active ?? t.active} onCheckedChange={(v) => setD(t.event_type, "active", v)} /></div>
                          <Button data-testid={`sms-save-${t.event_type}`} size="sm" onClick={() => saveTpl(t)} className="bg-emerald-600 hover:bg-emerald-700"><Save className="w-4 h-4 mr-1" /> Save</Button>
                        </div>
                      )}
                    </div>
                    <Textarea data-testid={`sms-body-${t.event_type}`} disabled={!isSuper} value={d.body ?? t.body} onChange={(e) => setD(t.event_type, "body", e.target.value)} rows={3} className="mt-3 font-mono text-sm" />
                    <p className="mt-1 text-[11px] text-gray-400">Placeholders like {"{tmf_id}"}, {"{amount}"}, {"{balance}"}, {"{code}"}, {"{ussd}"} are filled from the ledger automatically.</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-display font-bold text-xl text-emerald-950">Recent Notifications</h2>
              <Button data-testid="sms-retry-all-btn" size="sm" variant="outline" onClick={() => retry()} className="border-amber-300 text-amber-700 hover:bg-amber-50"><RefreshCw className="w-4 h-4 mr-1" /> Retry all failed</Button>
            </div>
            <p className="text-xs text-gray-400 mb-3">Delivery reports from the gateway move messages Sent → Delivered/Failed automatically. Failed messages retry up to {dash.max_retries ?? 3} times, then are marked permanently failed.</p>
            <div className="mt-1 rounded-2xl border border-emerald-100 bg-white overflow-hidden">
              <div className="max-h-96 overflow-y-auto divide-y divide-emerald-50">
                {notes.length === 0 ? <div className="p-6 text-sm text-gray-500 text-center">No SMS notifications yet.</div> :
                  notes.map((n) => (
                    <div key={n.id} data-testid="sms-note-row" className="p-4 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-100">{n.event_type}</Badge>
                          <span className="text-xs text-gray-400">{n.mobile}</span>
                          {n.simulated && <span className="text-[10px] text-amber-600">simulated</span>}
                        </div>
                        <p className="mt-1 text-sm text-gray-700 truncate">{n.message}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {n.retry_count > 0 && <span className="text-[10px] text-gray-400">retries: {n.retry_count}</span>}
                        {(n.delivery_status === "failed" || n.delivery_status === "failed_permanent") && (
                          <Button data-testid={`sms-retry-${n.id}`} size="sm" variant="ghost" className="h-7 px-2 text-amber-700 hover:bg-amber-50" onClick={() => retry(n.id)}><RefreshCw className="w-3.5 h-3.5" /></Button>
                        )}
                        <Badge data-testid="sms-note-status" className={statusClass(n.delivery_status)}>{n.delivery_status}</Badge>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
